import React, { useState, useEffect } from "react";
import { Search, Check, Trash2, Filter, X } from "lucide-react";
import reqProduto from "../../services/reqMultiTenant/reqProduto";
import Notification from "../toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckSquare, faSquare } from "@fortawesome/free-regular-svg-icons";
import CustomPagination from "../paginate";
import Switch from "@mui/material/Switch";
import libFloat from "../../util/libFloat";

const COMPONENT_PARAMS = [
    { id: 1, label: "Descrição", value: "nome" },
    { id: 2, label: "Ean", value: "ean" },
    { id: 3, label: "Referência", value: "referencia" },
];

export function ProductSearch({ 
    onSelectProduct, 
    onRemoveProduct, 
    customActionIcon = null, 
    customActionTitle = "Selecionar Produto",
    autoFocus = false,
    linkedIds = [], // Array de IDs já selecionados para trocar o ícone nativamente
    excludedIds = []
}) {
    // Parâmetros de Filtro
    const [paramSelected, setParamSelected] = useState(COMPONENT_PARAMS[0]);
    const [activeOnly, setActiveOnly] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);

    // Engenharia de Busca Paginada
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [page, setPage] = useState(1);
    const [totalSearchPages, setTotalSearchPages] = useState(0);

    // Reseta quando for montado
    useEffect(() => {
        setSearchTerm("");
        setSearchResults([]);
        setPage(1);
        setTotalSearchPages(0);
    }, []);

    const handleSearch = async (targetPage = 1) => {
        setIsSearching(true);
        try {
            const flagAtivo = activeOnly ? "SIM" : "NAO";
            const res = await reqProduto.listPaginate(
                paramSelected.value, 
                searchTerm, 
                flagAtivo, 
                targetPage
            );
            
            const list = res?.data?.data || res?.data || [];
            const resultList = Array.isArray(list) ? list : [];
            setSearchResults(resultList);
            
            let total = 0;
            // Se o backend enviar meta-dados estilo Laravel:
            if (res?.data?.last_page) {
                total = res.data.last_page;
            } else if (res?.data?.total) {
                total = Math.ceil(res.data.total / 15);
            } else {
                // Se for um Array nativo do Delphi (frameless)
                // Se retornou menos que 15 registros, sabemos que chegamos no fim
                if (resultList.length < 15) {
                    total = targetPage; 
                } else {
                    total = 0; // Deixa 0 para manter o botão "NEXT" habilitado na CustomPagination
                }
            }
            
            setTotalSearchPages(total);
            setPage(targetPage);
            
        } catch (error) {
            console.error("Erro na busca de produtos API", error);
            Notification.showToast({
                message: "Erro ao consultar produtos.",
                type: "error"
            });
        } finally {
            setIsSearching(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleSearch(1);
        }
    };

    const visibleResults = searchResults.filter((prod) => !excludedIds.includes(prod.id_produto || prod.id));

    return (
        <div className="flex flex-col gap-3 rounded-md border border-gray-200 p-0 overflow-hidden bg-white shadow-sm h-full min-h-0">
            
            {/* Bloco de Pesquisa Superior */}
            <div className="bg-gray-50/80 border-b p-2 flex flex-col gap-2 shrink-0">
                
                {/* Input, Lupa e Botão Filtro */}
                <div className="flex gap-2 items-end">
                    <div className="flex-1">
                        <label className="text-xs text-secondary mb-1 block">
                            Pesquisar por {paramSelected.label}...
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Search size={16} className="text-gray-400" />
                            </div>
                            <input
                                type="text"
                                className="form-control pl-9 h-8 text-sm w-full rounded border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                placeholder="Digite e aperte ENTER..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
                                onKeyDown={handleKeyDown}
                                autoFocus={autoFocus}
                            />
                        </div>
                    </div>
                    
                    <button 
                        type="button" 
                        title="Filtros e Configurações de Busca"
                        className={`btn btn-sm h-8 px-3 flex items-center gap-1 transition-colors ${showFilters ? 'btn-secondary' : 'btn-outline-secondary'}`}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        {showFilters ? <X size={16} /> : <Filter size={16} />}
                        <span className="hidden sm:inline">{showFilters ? "Fechar" : "Filtros"}</span>
                    </button>

                    <button 
                        type="button" 
                        className="btn btn-primary h-8 flex items-center justify-center min-w-[40px] px-2"
                        onClick={() => handleSearch(1)}
                        disabled={isSearching}
                    >
                        {isSearching ? <span className="spinner-border spinner-border-sm"></span> : <Search size={16} />}
                    </button>
                </div>

                {/* Painel de Filtros Expansível */}
                {showFilters && (
                    <div className="flex flex-wrap items-center justify-between gap-4 bg-white border border-gray-200 rounded p-3 mt-1 shadow-sm">
                        <div className="flex items-center flex-wrap gap-4">
                            {COMPONENT_PARAMS.map((option) => (
                                <div key={option.id} className="cursor-pointer" onClick={() => setParamSelected(option)}>
                                    <label className="cursor-pointer m-0 flex items-center gap-2 text-sm text-gray-700">
                                        <FontAwesomeIcon
                                            icon={paramSelected.value === option.value ? faCheckSquare : faSquare}
                                            className="text-primary"
                                        />
                                        {option.label}
                                    </label>
                                </div>
                            ))}
                        </div>

                        <div className="flex items-center gap-2 border-l pl-4 border-gray-200">
                            <label className="text-sm text-gray-700 m-0 font-medium">Apenas Ativos</label>
                            <Switch
                                size="small"
                                checked={activeOnly}
                                onChange={(e) => setActiveOnly(e.target.checked)}
                                color="success"
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Container da Tabela e Paginação */}
            <div className="flex-1 flex flex-col min-h-0">
                {visibleResults.length === 0 && !isSearching && (
                    <div className="flex-1 flex items-center justify-center text-gray-500 text-xs italic">
                        Nenhum produto listado. Realize sua pesquisa acima.
                    </div>
                )}
                
                {visibleResults.length > 0 && !isSearching && (
                    <>
                        <div className="flex-1 overflow-y-auto">
                            <table className="table table-sm table-hover mb-0 w-full border-b">
                                <thead className="color-table sticky top-0 z-10 shadow-sm">
                                    <tr>
                                        <th className="text-left font-semibold py-2 border-b"><span className="text-color-table">Id</span></th>
                                        <th className="text-left font-semibold py-2 border-b"><span className="text-color-table">Descrição</span></th>
                                        <th className="text-center font-semibold py-2 border-b"><span className="text-color-table">Referência</span></th>
                                        <th className="text-right font-semibold py-2 border-b"><span className="text-color-table">Valor</span></th>
                                        <th className="text-center font-semibold py-2 border-b w-24"><span className="text-color-table">Ação</span></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleResults.map((prod) => {
                                        const prodId = prod.id_produto || prod.id;
                                        const isLinked = linkedIds.includes(prodId);

                                        return (
                                            <tr key={prodId} className="hover:bg-blue-50 group cursor-pointer" onClick={() => !isLinked && onSelectProduct(prod)}>
                                                <td className="text-left align-middle text-gray-500 text-sm">{prodId}</td>
                                                <td className="text-left align-middle font-medium py-2">{prod.nome}</td>
                                                <td className="text-center align-middle">{prod.referencia || "-"}</td>
                                                <td className="text-right align-middle text-emerald-600 font-semibold">{libFloat.strToCurrency(prod.valor || 0)}</td>
                                                <td className="text-center align-middle">
                                                    {isLinked ? (
                                                        <button 
                                                            type="button"
                                                            title="Já vinculado. Clique para remover."
                                                            className="btn btn-sm btn-success p-1 rounded flex items-center justify-center hover:bg-red-500 hover:border-red-500 transition-colors group/btn w-7 h-7 mx-auto"
                                                            onClick={(e) => {
                                                                if (onRemoveProduct) {
                                                                    e.stopPropagation();
                                                                    onRemoveProduct(prodId);
                                                                }
                                                            }}
                                                        >
                                                            <span className="block group-hover/btn:hidden"><Check size={16} /></span>
                                                            <span className="hidden group-hover/btn:block text-white"><Trash2 size={16} /></span>
                                                        </button>
                                                    ) : (
                                                        <button 
                                                            type="button"
                                                            title={customActionTitle}
                                                            className="btn btn-sm btn-outline-primary p-1 rounded opacity-70 group-hover:opacity-100 transition-opacity"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onSelectProduct(prod);
                                                            }}
                                                        >
                                                            {customActionIcon || <Search size={16} />}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {searchResults.length > 0 && (
                            <div className="flex justify-center p-2 bg-gray-50 border-t shrink-0">
                                <CustomPagination
                                    totalPage={totalSearchPages}
                                    onChangePage={(e, newPage) => handleSearch(newPage)}
                                    page={page}
                                    data={searchResults}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default ProductSearch;
